import { securityConfig } from '../config/security'
import { captureMessage } from '../config/sentry'
import { sessionSync } from './sessionSync'

// Session storage keys
const SESSION_WARNED_KEY = 'session_warned'
const LAST_ACTIVITY_KEY = 'last_activity'

export class SessionManager {
  private timeoutId: NodeJS.Timeout | null = null
  private warningTimeoutId: NodeJS.Timeout | null = null
  private onTimeout: (() => void) | null = null
  private onWarning: (() => void) | null = null
  
  constructor() {
    // Listen for user activity
    if (typeof window !== 'undefined') {
      this.setupActivityListeners()
    }
  }
  
  /**
   * Initialize session management with callbacks
   */
  init(options: {
    onTimeout: () => void
    onWarning?: () => void
  }) {
    this.onTimeout = options.onTimeout
    this.onWarning = options.onWarning || null
    
    // Update activity and start timer
    this.updateActivity()
    this.startTimer()
    
    // Check if returning from another tab
    this.checkExistingSession()
    
    // Listen for session sync events
    sessionSync.on('logout', () => {
      this.handleTimeout()
    })
    
    sessionSync.on('extend', () => {
      this.updateActivity()
    })
    
    sessionSync.on('activity', () => {
      this.updateActivity()
    })
  }
  
  /**
   * Setup event listeners for user activity
   */
  private setupActivityListeners() {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    
    events.forEach(event => {
      window.addEventListener(event, () => this.updateActivity(), {
        passive: true,
        capture: true,
      })
    })
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkExistingSession()
      }
    })
    
    // Listen for storage events from other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === LAST_ACTIVITY_KEY) {
        this.checkExistingSession()
      }
    })
  }
  
  /**
   * Update last activity timestamp
   */
  updateActivity() {
    const now = Date.now()
    localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
    localStorage.removeItem(SESSION_WARNED_KEY)
    
    // Notify other tabs
    sessionSync.notifyActivity()
    
    // Restart the timer
    this.startTimer()
  }
  
  /**
   * Start or restart the session timer
   */
  private startTimer() {
    // Clear existing timers
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId)
    }
    
    const sessionTimeout = securityConfig.sessionTimeout
    const warningTime = sessionTimeout - (5 * 60 * 1000) // Warn 5 minutes before timeout
    
    // Set warning timer (if callback provided)
    if (this.onWarning && warningTime > 0) {
      this.warningTimeoutId = setTimeout(() => {
        const hasWarned = localStorage.getItem(SESSION_WARNED_KEY)
        if (!hasWarned) {
          localStorage.setItem(SESSION_WARNED_KEY, 'true')
          this.onWarning?.()
        }
      }, warningTime)
    }
    
    // Set timeout timer
    this.timeoutId = setTimeout(() => {
      this.handleTimeout()
    }, sessionTimeout)
  }
  
  /**
   * Handle session timeout
   */
  private handleTimeout() {
    captureMessage('Session timed out', 'info')
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    localStorage.removeItem(SESSION_WARNED_KEY)
    
    // Notify other tabs
    sessionSync.notifyLogout()
    
    this.onTimeout?.()
  }
  
  /**
   * Check if session is still valid when returning to app
   */
  private checkExistingSession() {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
    if (!lastActivity) return
    
    const lastActivityTime = parseInt(lastActivity, 10)
    const now = Date.now()
    const elapsed = now - lastActivityTime
    
    if (elapsed > securityConfig.sessionTimeout) {
      // Session expired while away
      this.handleTimeout()
    } else {
      // Session still valid, restart timer with remaining time
      const remaining = securityConfig.sessionTimeout - elapsed
      
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
      }
      
      this.timeoutId = setTimeout(() => {
        this.handleTimeout()
      }, remaining)
    }
  }
  
  /**
   * Extend the session (e.g., after user confirms they're still active)
   */
  extendSession() {
    this.updateActivity()
    captureMessage('Session extended by user', 'info')
    
    // Notify other tabs
    sessionSync.notifyExtend()
  }
  
  /**
   * Clear the session
   */
  clear() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId)
    }
    
    localStorage.removeItem(LAST_ACTIVITY_KEY)
    localStorage.removeItem(SESSION_WARNED_KEY)
  }
  
  /**
   * Get remaining session time in milliseconds
   */
  getRemainingTime(): number {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
    if (!lastActivity) return 0
    
    const lastActivityTime = parseInt(lastActivity, 10)
    const elapsed = Date.now() - lastActivityTime
    const remaining = securityConfig.sessionTimeout - elapsed
    
    return Math.max(0, remaining)
  }
  
  /**
   * Format remaining time as human-readable string
   */
  getFormattedRemainingTime(): string {
    const remaining = this.getRemainingTime()
    const minutes = Math.floor(remaining / 60000)
    const seconds = Math.floor((remaining % 60000) / 1000)
    
    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''}`
  }
}

// Singleton instance
export const sessionManager = new SessionManager()